import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, currency, formatApiError, resolveImage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [address, setAddress] = useState({
    full_name: user?.name || "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "India",
  });
  const [payment, setPayment] = useState("cod");
  const [notes, setNotes] = useState("");

  const shipping = subtotal >= 999 ? 0 : 49;
  const tax = Math.round(subtotal * 0.05);
  const total = subtotal + shipping + tax;

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-4xl text-[#1e392a]">Your cart is empty.</h1>
        <Button
          onClick={() => navigate("/shop")}
          className="mt-6 rounded-full bg-[#1e392a] hover:bg-[#14251b] text-white h-12 px-8"
        >
          Continue shopping
        </Button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        address,
        payment_method: payment,
        notes,
      });
      clear();
      navigate(`/order/${data.id}`, { state: data });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <p className="overline">Checkout</p>
      <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-[#1e392a] tracking-tight">
        Confirm your ritual.
      </h1>

      <form onSubmit={submit} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          <section>
            <h2 className="font-serif text-2xl text-[#1e392a] mb-6">Shipping address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-[#8a8d86]">Full name</Label>
                <Input
                  data-testid="checkout-name-input"
                  required
                  value={address.full_name}
                  onChange={(e) => setAddress({ ...address, full_name: e.target.value })}
                  className="bg-white border-[#e6e2d8] mt-2 h-11"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8a8d86]">Phone</Label>
                <Input
                  data-testid="checkout-phone-input"
                  required
                  value={address.phone}
                  onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                  className="bg-white border-[#e6e2d8] mt-2 h-11"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8a8d86]">Country</Label>
                <Input
                  value={address.country}
                  onChange={(e) => setAddress({ ...address, country: e.target.value })}
                  className="bg-white border-[#e6e2d8] mt-2 h-11"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-[#8a8d86]">Address line 1</Label>
                <Input
                  data-testid="checkout-line1-input"
                  required
                  value={address.line1}
                  onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                  className="bg-white border-[#e6e2d8] mt-2 h-11"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-[#8a8d86]">Address line 2 (optional)</Label>
                <Input
                  value={address.line2}
                  onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                  className="bg-white border-[#e6e2d8] mt-2 h-11"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8a8d86]">City</Label>
                <Input
                  data-testid="checkout-city-input"
                  required
                  value={address.city}
                  onChange={(e) => setAddress({ ...address, city: e.target.value })}
                  className="bg-white border-[#e6e2d8] mt-2 h-11"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8a8d86]">State</Label>
                <Input
                  data-testid="checkout-state-input"
                  required
                  value={address.state}
                  onChange={(e) => setAddress({ ...address, state: e.target.value })}
                  className="bg-white border-[#e6e2d8] mt-2 h-11"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8a8d86]">Postal code</Label>
                <Input
                  data-testid="checkout-postal-input"
                  required
                  value={address.postal_code}
                  onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
                  className="bg-white border-[#e6e2d8] mt-2 h-11"
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#1e392a] mb-6">Payment method</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
              <label
                htmlFor="cod"
                className={
                  "flex items-start gap-3 p-4 border rounded-md cursor-pointer transition-colors " +
                  (payment === "cod" ? "border-[#1e392a] bg-white" : "border-[#e6e2d8] bg-white/60")
                }
              >
                <RadioGroupItem id="cod" value="cod" data-testid="pay-cod-radio" />
                <div>
                  <p className="font-medium text-[#1e392a]">Cash on Delivery</p>
                  <p className="text-xs text-[#8a8d86] mt-1">
                    Pay in cash when your order arrives. Available across India.
                  </p>
                </div>
              </label>
              <label
                htmlFor="mock_card"
                className={
                  "flex items-start gap-3 p-4 border rounded-md cursor-pointer transition-colors " +
                  (payment === "mock_card" ? "border-[#1e392a] bg-white" : "border-[#e6e2d8] bg-white/60")
                }
              >
                <RadioGroupItem id="mock_card" value="mock_card" data-testid="pay-card-radio" />
                <div>
                  <p className="font-medium text-[#1e392a]">Card / UPI (Demo)</p>
                  <p className="text-xs text-[#8a8d86] mt-1">
                    Simulated payment for demo purposes. Real gateway coming soon.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#1e392a] mb-6">Order notes (optional)</h2>
            <textarea
              data-testid="checkout-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full p-4 bg-white border border-[#e6e2d8] rounded-md text-sm focus:outline-none focus:border-[#1e392a]"
              placeholder="Gift wrap, delivery instructions…"
            />
          </section>
        </div>

        <aside className="lg:col-span-5 h-fit lg:sticky lg:top-24">
          <div className="bg-white border border-[#e6e2d8] rounded-md p-8">
            <h2 className="font-serif text-2xl text-[#1e392a] mb-6">Your order</h2>
            <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3">
                  <div className="w-16 h-16 rounded bg-[#efeadd] overflow-hidden flex-shrink-0">
                    {i.image_url && (
                      <img src={resolveImage(i.image_url)} alt={i.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg text-[#1e392a] truncate">{i.name}</p>
                    <p className="text-xs text-[#8a8d86]">Qty {i.quantity}</p>
                  </div>
                  <p className="text-sm text-[#1e392a]">{currency(i.price * i.quantity)}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-[#e6e2d8] space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#8a8d86]">Subtotal</span>
                <span>{currency(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8d86]">Shipping</span>
                <span>{shipping === 0 ? "Free" : currency(shipping)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8d86]">Estimated tax (5%)</span>
                <span>{currency(tax)}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-[#e6e2d8]">
                <span className="font-serif text-xl text-[#1e392a]">Total</span>
                <span data-testid="checkout-total" className="font-serif text-2xl text-[#1e392a]">
                  {currency(total)}
                </span>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="checkout-place-order-btn"
              disabled={submitting}
              className="w-full mt-8 h-12 rounded-full bg-[#1e392a] hover:bg-[#14251b] text-white"
            >
              {submitting ? "Placing order…" : `Place order · ${currency(total)}`}
            </Button>
            <p className="text-xs text-[#8a8d86] mt-4 text-center">
              Free shipping over ₹999 · 14-day returns
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}
